import type { DetailedTicket, RenewType, TicketState } from '../../types';

type TicketStatusInput = Pick<DetailedTicket, 'renewType' | 'cancelReceiptAt' | 'ticketState'>;

type PodBottomStatusVariant = 'default' | 'success' | 'info' | 'warning';

type PodBottomStatus = {
  variant: PodBottomStatusVariant;
  translationKey: string;
  showAmount?: boolean;
};

type PodBottomStatusInput = TicketStatusInput & {
  winnings: number;
  /** Winnings (in cents) at or above which the jackpot message replaces the amount. */
  jackpotWinThreshold?: number;
};

type PodBottomStatusRule = {
  matches: (input: PodBottomStatusInput) => boolean;
  resolve: (input: PodBottomStatusInput) => PodBottomStatus;
};

type RenewalPodLabel = {
  translationKey: string;
  variant: 'accent' | 'disabled';
};

const SETTLED_TICKET_STATES: readonly TicketState[] = ['CLOSED', 'VOIDED', 'OUT_OF_SYNC'];

function isSettledTicket(ticketState: TicketState): boolean {
  return SETTLED_TICKET_STATES.includes(ticketState);
}

function isHuntCancelledByUser({ renewType, cancelReceiptAt }: TicketStatusInput): boolean {
  return renewType === 'Hunt' && !!cancelReceiptAt;
}

function isHuntEndedByJackpotWin({ renewType, cancelReceiptAt, ticketState }: TicketStatusInput): boolean {
  return renewType === 'Hunt' && !cancelReceiptAt && ticketState === 'CLOSED';
}

function getRenewalPodLabel(input: TicketStatusInput): RenewalPodLabel | null {
  if (isHuntCancelledByUser(input)) {
    return { translationKey: 'renew_status.hunt_cancelled', variant: 'disabled' };
  }

  if (isHuntEndedByJackpotWin(input)) {
    return { translationKey: 'renew_status.hunt_ended', variant: 'disabled' };
  }

  if (input.renewType === 'Subscription' && input.cancelReceiptAt) {
    return { translationKey: 'renew_status.subscription_cancelled', variant: 'disabled' };
  }

  if (input.renewType && input.renewType !== 'Weeks') {
    return { translationKey: `renew_type.${input.renewType}`, variant: 'accent' };
  }

  return null;
}

function getPodWinningsLabelKey(renewType?: RenewType): 'pod_status.all_time_winnings' | 'pod_status.winnings' {
  return renewType === 'Weeks' ? 'pod_status.winnings' : 'pod_status.all_time_winnings';
}

const POD_BOTTOM_STATUS_RULES: PodBottomStatusRule[] = [
  {
    matches: ({ ticketState }) => ticketState === 'VOIDED',
    resolve: () => ({ variant: 'warning', translationKey: 'pod_status.voided' }),
  },
  {
    matches: ({ ticketState, winnings, jackpotWinThreshold }) => isSettledTicket(ticketState) && jackpotWinThreshold !== undefined && winnings >= jackpotWinThreshold,
    resolve: () => ({ variant: 'success', translationKey: 'pod_status.jackpot_won' }),
  },
  {
    matches: ({ ticketState, winnings }) => isSettledTicket(ticketState) && winnings > 0,
    resolve: ({ renewType }) => ({
      variant: 'success',
      translationKey: getPodWinningsLabelKey(renewType),
      showAmount: true,
    }),
  },
];

function getPodBottomStatus(input: PodBottomStatusInput): PodBottomStatus | null {
  return POD_BOTTOM_STATUS_RULES.find(rule => rule.matches(input))?.resolve(input) ?? null;
}

export { getPodBottomStatus, getPodWinningsLabelKey, getRenewalPodLabel, isHuntCancelledByUser, isHuntEndedByJackpotWin, isSettledTicket };
export type { PodBottomStatus, PodBottomStatusInput, PodBottomStatusVariant, RenewalPodLabel, TicketStatusInput };
